/**
 * How screener reads retry (docs/stock-screener.md, The read path).
 * The API answers 503 with Retry-After when its database is slow or full, so
 * a read retries at most twice, only on a 503 or a network failure (including
 * the 20 s timeout), after the server's Retry-After or a short backoff, plus
 * jitter so many screens don't come back at the same moment. A 4xx or a 500
 * never retries: asking again can't fix it.
 */

export const SCREENER_READ_TIMEOUT_MS = 20_000;
const MAX_RETRIES = 2;
const MAX_BACKOFF_MS = 8_000;
const JITTER_MS = 1_000;

interface RetryableShape {
  status?: number;
  body?: { retryAfterSeconds?: unknown };
  response?: {
    status?: number;
    data?: { retryAfterSeconds?: unknown };
    headers?: Record<string, unknown>;
  };
}

const statusOf = (error: unknown): number | undefined => {
  const shape = error as RetryableShape | null;
  return shape?.status ?? shape?.response?.status;
};

const retryAfterSecondsOf = (error: unknown): number | null => {
  const shape = error as RetryableShape | null;
  const candidates = [
    shape?.body?.retryAfterSeconds,
    shape?.response?.data?.retryAfterSeconds,
    shape?.response?.headers?.["retry-after"],
  ];
  for (const candidate of candidates) {
    const seconds = Number(candidate);
    if (
      candidate !== undefined &&
      candidate !== null &&
      Number.isFinite(seconds) &&
      seconds >= 0
    )
      return seconds;
  }
  return null;
};

// No status (or 0, how the run hooks record it) is a network failure or a timeout.
export const isRetryableScreenerError = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === undefined || status === 0 || status === 503;
};

export const screenerRetry = (failureCount: number, error: unknown): boolean =>
  failureCount < MAX_RETRIES && isRetryableScreenerError(error);

export const screenerRetryDelay = (attempt: number, error: unknown): number => {
  const retryAfter = retryAfterSecondsOf(error);
  const base =
    retryAfter !== null
      ? retryAfter * 1000
      : Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
  return base + Math.random() * JITTER_MS;
};
